/**
 * Componente AreaTimeline
 * Timeline visual de atividades de uma área da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 31/10/2025
 * @version 1.0.0
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Activity, 
  Plus, 
  Edit, 
  UserPlus, 
  UserMinus, 
  ArrowRightLeft,
  Building2,
  User,
  Clock,
  Loader2,
  RefreshCw,
  XCircle
} from 'lucide-react';
import api from '@/services/api';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface AreaTimelineProps {
  areaId: string;
  className?: string;
  maxItems?: number;
  showHeader?: boolean;
}

interface AreaActivity {
  id: string;
  action: string;
  description: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  metadata?: any;
  createdAt: string;
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

const getActionLabel = (action: string): string => {
  const labels: Record<string, string> = {
    CREATED: 'Área Criada',
    UPDATED: 'Área Atualizada',
    USER_LINKED: 'Usuário Vinculado',
    USER_UNLINKED: 'Usuário Desvinculado',
    USER_TRANSFERRED: 'Usuário Transferido',
    MANAGER_CHANGED: 'Gestor Alterado',
    STATUS_CHANGED: 'Status Alterado',
  };
  return labels[action] || action;
};

const getActionColor = (action: string): string => {
  const colors: Record<string, string> = {
    CREATED: 'bg-blue-100 text-blue-800',
    UPDATED: 'bg-yellow-100 text-yellow-800',
    USER_LINKED: 'bg-green-100 text-green-800',
    USER_UNLINKED: 'bg-red-100 text-red-800',
    USER_TRANSFERRED: 'bg-purple-100 text-purple-800',
    MANAGER_CHANGED: 'bg-orange-100 text-orange-800',
    STATUS_CHANGED: 'bg-gray-100 text-gray-800',
  };
  return colors[action] || 'bg-gray-100 text-gray-800';
};

const getActionIcon = (action: string): React.ComponentType<any> => {
  const icons: Record<string, React.ComponentType<any>> = {
    CREATED: Plus,
    UPDATED: Edit,
    USER_LINKED: UserPlus,
    USER_UNLINKED: UserMinus,
    USER_TRANSFERRED: ArrowRightLeft,
    MANAGER_CHANGED: User,
    STATUS_CHANGED: Edit,
  };
  return icons[action] || Activity;
};

const formatActivityDate = (date: string): string => {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins} min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays} dia(s) atrás`;
  
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============================================================================
// COMPONENTE TIMELINE ITEM
// ============================================================================

interface TimelineItemProps {
  activity: AreaActivity;
  isLast: boolean;
}

const TimelineItem: React.FC<TimelineItemProps> = ({ activity, isLast }) => {
  const IconComponent = getActionIcon(activity.action);
  const actionColor = getActionColor(activity.action);
  const actionLabel = getActionLabel(activity.action);
  const formattedDate = formatActivityDate(activity.createdAt);

  return (
    <div className="relative flex items-start space-x-3 pb-4">
      {/* Linha vertical da timeline */}
      {!isLast && (
        <div className="absolute left-4 top-8 h-full w-0.5 bg-gray-200" />
      )}
      
      {/* Ícone da ação */}
      <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gray-50 shadow-sm">
        <IconComponent className="h-4 w-4 text-gray-600" />
      </div>
      
      {/* Conteúdo da atividade */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Badge da ação */}
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionColor}`}>
              {actionLabel}
            </span>
            
            {/* Usuário */}
            {activity.user && (
              <div className="flex items-center text-sm text-gray-500">
                <User className="h-3 w-3 mr-1" />
                <span>{activity.user.name}</span>
              </div>
            )}
          </div>
          
          {/* Timestamp */}
          <div className="flex items-center text-xs text-gray-400">
            <Clock className="h-3 w-3 mr-1" />
            <span>{formattedDate}</span>
          </div>
        </div>
        
        {/* Descrição da atividade */}
        <div className="mt-1">
          <p className="text-sm text-gray-900">{activity.description}</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const AreaTimeline: React.FC<AreaTimelineProps> = ({ 
  areaId, 
  className = '',
  maxItems = 50,
  showHeader = true
}) => {
  // Query para buscar logs da área
  const { 
    data: activities = [], 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['area-activities', areaId],
    queryFn: async () => {
      const response = await api.get(`/company-areas/${areaId}/activities`);
      return response.data.data || [];
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    staleTime: 10000, // Considerar dados frescos por 10 segundos
  });

  // Limitar número de itens se especificado
  const displayActivities = maxItems ? activities.slice(0, maxItems) : activities;

  // Estados de loading e erro
  if (isLoading) {
    return (
      <div className={`bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border border-border-glass p-6 ${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Activity className="h-5 w-5 mr-2 text-secondary" />
              Timeline de Atividades
            </h3>
          </div>
        )}
        
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-secondary" />
          <span className="ml-2 text-sm text-text-muted">Carregando timeline...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border border-border-glass p-6 ${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Activity className="h-5 w-5 mr-2 text-secondary" />
              Timeline de Atividades
            </h3>
          </div>
        )}
        
        <div className="text-center py-8">
          <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-sm text-text-muted mb-4">
            Erro ao carregar timeline de atividades
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 border border-border-glass shadow-glass text-sm leading-4 font-medium rounded-button text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Estado vazio
  if (displayActivities.length === 0) {
    return (
      <div className={`bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border border-border-glass p-6 ${className}`}>
        {showHeader && (
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Activity className="h-5 w-5 mr-2 text-secondary" />
              Timeline de Atividades
            </h3>
          </div>
        )}
        
        <div className="text-center py-8">
          <Activity className="h-12 w-12 text-text-muted mx-auto mb-4 opacity-50" />
          <p className="text-sm text-text-muted">
            Nenhuma atividade registrada para esta área
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border border-border-glass ${className}`}>
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between p-6 border-b border-border-glass">
          <h3 className="text-lg font-semibold text-text-primary flex items-center">
            <Activity className="h-5 w-5 mr-2 text-secondary" />
            Timeline de Atividades
          </h3>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-text-muted">
              {activities.length} {activities.length === 1 ? 'atividade' : 'atividades'}
            </span>
            
            <button
              onClick={() => refetch()}
              className="p-1 text-text-muted hover:text-text-primary transition-colors"
              title="Atualizar timeline"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Timeline */}
      <div className="p-6">
        <div className="flow-root">
          <ul className="-mb-8">
            {displayActivities.map((activity, index) => (
              <li key={activity.id}>
                <TimelineItem 
                  activity={activity} 
                  isLast={index === displayActivities.length - 1}
                />
              </li>
            ))}
          </ul>
        </div>
        
        {/* Indicador de mais itens */}
        {maxItems && activities.length > maxItems && (
          <div className="mt-4 pt-4 border-t border-border-glass text-center">
            <p className="text-sm text-text-muted">
              Mostrando {maxItems} de {activities.length} atividades
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaTimeline;

